import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import RemoveIcon from '@material-ui/icons/RemoveCircle';

import FieldSet from '../../../common/FieldSets/FieldSet';
import FieldRow from '../../../common/FieldSets/FieldRow.styled';
import Autocomplete, {
  ParticipantSuggestionItem,
} from '../../../common/FieldSets/Autocomplete';
import {
  LegendContainer,
  ClickableLegendContainer,
  Button,
  ParticipantsContainer,
  PurpleArrowForwardIcon,
  AddIcon,
} from './ParticipantsFieldSet.styled';
import { FadedContainer, Icon, Text } from '../NoContent.styled';
import GroupParticipantsList from '../GroupParticipantsList';

const NoParticipants = () => (
  <FadedContainer compact>
    <Icon>
      <span role="img" aria-label="point_up">
        👆
      </span>
    </Icon>
    <Text>
      This group doesn&apos;t currently have any participants. Start by adding
      some above!
    </Text>
  </FadedContainer>
);

const ParticipantsFieldSet = ({
  selected,
  isCurrentUserSelected,
  studentsWithNoGroup,
  onSelect,
  onSelectCurrentUser,
  onRemove,
  isReadOnly,
  search,
  onStudentsNotInGroupClick,
  usersAdded,
}) => {
  const [participants, setParticipants] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [availableStudents, setAvailableStudents] = useState([]);
  useEffect(() => {
    const selectedSet = new Set(selected.map((student) => student.id));
    const newAvailable = studentsWithNoGroup.filter(
      (student) => !selectedSet.has(student.id)
    );
    setAvailableStudents(newAvailable);
  }, [selected, studentsWithNoGroup]);

  const numStudentsAvailable = availableStudents.length;
  const pluralizedStudents =
    numStudentsAvailable === 1 ? 'student' : 'students';

  const allStudentsWithGroup = (
    <LegendContainer>All students assigned to groups</LegendContainer>
  );

  const legend =
    numStudentsAvailable === 0 ? (
      allStudentsWithGroup
    ) : (
      <ClickableLegendContainer onClick={onStudentsNotInGroupClick}>
        <span>{`${numStudentsAvailable} ${pluralizedStudents} not yet in a group`}</span>
        <PurpleArrowForwardIcon />
      </ClickableLegendContainer>
    );

  const onChange = async (query) => {
    if (query) {
      setIsFetching(true);
      const results = await search(query);
      if (results !== null) {
        setParticipants(results);
        setIsFetching(false);
      }
    } else {
      setParticipants([]);
    }
  };

  const addMyselfButton =
    !isCurrentUserSelected && !isReadOnly ? (
      <FieldRow>
        <Button variant="text" onClick={onSelectCurrentUser}>
          <AddIcon />
          Add yourself
        </Button>
      </FieldRow>
    ) : null;

  const participantsContent =
    selected.length > 0 ? (
      <React.Fragment>
        <FieldRow>
          <GroupParticipantsList
            users={selected}
            compact
            itemAction={{
              label: <RemoveIcon />,
              tooltip: 'Remove from this group',
              onAction: onRemove,
            }}
            isReadOnly={isReadOnly}
            usersAdded={usersAdded}
          />
        </FieldRow>
        {addMyselfButton}
      </React.Fragment>
    ) : (
      <NoParticipants />
    );

  const searchParticipants = isReadOnly ? null : (
    <FieldRow>
      <Autocomplete
        id="group-participant-autocomplete"
        placeholder="Search for participants"
        dataset={participants}
        SuggestionItem={ParticipantSuggestionItem}
        onItemSelect={onSelect}
        onInputChange={onChange}
        isFetching={isFetching}
      />
    </FieldRow>
  );

  return (
    <FieldSet title="Participants" legend={legend}>
      {searchParticipants}
      <ParticipantsContainer>{participantsContent}</ParticipantsContainer>
    </FieldSet>
  );
};

ParticipantsFieldSet.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.PropTypes.shape({})).isRequired,
  usersAdded: PropTypes.shape({}).isRequired,
  isCurrentUserSelected: PropTypes.bool.isRequired,
  studentsWithNoGroup: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSelect: PropTypes.func.isRequired,
  onSelectCurrentUser: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  search: PropTypes.func.isRequired,
  onStudentsNotInGroupClick: PropTypes.func.isRequired,
};

export default ParticipantsFieldSet;
